import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { CPF_MASK, CNPJ_MASK, PHONE_MASK } from 'v3/utils/masks';
import api from 'services/api';
import { useValidation } from 'hooks';
import Radio from 'v3/components/Radio';
import { useSnackbar } from 'v3/components/Snackbar';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import Input from 'v3/components/Input';
import Text from 'v3/components/Text';
import Select from 'v3/components/Select';
import { fetchLinkShippingCompany } from 'utils/fetches';
import { TravelContext } from '../../controller';

import { travelShippingCompanyDetailsSchema } from '../../../../ShippingCompany/shared/validator';

const [, useTravel] = TravelContext;

function ShippingCompanyDetails({ isOpen, onClose, shippingCompany, setShippingCompany }) {
  const travel = useTravel();

  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbar();
  const [errors, setErrors] = useState({});
  const [linkOption, setLinkOption] = useState([]);

  const [validationErrors, { validate }] = useValidation(travelShippingCompanyDetailsSchema);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  function getTypeId() {
    if (shippingCompany?.personType?.name) {
      return shippingCompany.personType.name === 'Jurídica' ? 3 : 2;
    }
    return shippingCompany?.type_id;
  }

  function getShipmentLinkId() {
    return (
      shippingCompany?.shipment_link?.id ??
      shippingCompany?.shipment_link_id ??
      shippingCompany?.shippingCompanies?.linkShippingCompany?.id
    );
  }

  async function verifySchema() {
    try {
      await travelShippingCompanyDetailsSchema.validate(
        {
          type_id: getTypeId(),
          cgccpf: shippingCompany.cgccpf,
          cgccpf_unmasked: shippingCompany?.cgccpf?.replace(/[.\-/]/g, ''),
          social_name: shippingCompany.social_name,
          phone: shippingCompany.phone,
          phone_unmasked: shippingCompany?.phone?.replace(/[^0-9]+/g, ''),
          shipment_link_id: getShipmentLinkId(),
        },
        { abortEarly: false },
      );
      setErrors({});
    } catch (error) {
      const errorResponse = {};

      if (error.inner) {
        error.inner.forEach((error) => {
          errorResponse[error.path] = error.message;
        });
        setErrors(errorResponse);
      }
    }
  }

  useEffect(() => {
    if (shippingCompany) {
      verifySchema();
    }
  }, [shippingCompany]);

  useEffect(() => {
    fetchLinkShippingCompany().then(setLinkOption);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!shippingCompany) return;
    if (!shippingCompany.personType && shippingCompany.type_id) {
      setShippingCompany({
        ...shippingCompany,
        personType: {
          name: shippingCompany.type_id === 3 ? 'Jurídica' : 'Física',
        },
      });
    }
  }, [shippingCompany?.type_id, shippingCompany?.personType, setShippingCompany]);

  useEffect(() => {
    const currentLinkId = getShipmentLinkId();
    if (!currentLinkId || shippingCompany?.shipment_link || !linkOption?.length) {
      return;
    }
    const matchedLink = linkOption.find((option) => option.id === currentLinkId);
    if (matchedLink) {
      setShippingCompany({
        ...shippingCompany,
        shipment_link: matchedLink,
        shipment_link_id: matchedLink.id,
      });
    }
  }, [
    linkOption,
    shippingCompany?.shipment_link_id,
    shippingCompany?.shipment_link,
    shippingCompany?.shippingCompanies?.linkShippingCompany?.id,
    setShippingCompany,
  ]);

  async function saveData() {
    const data = {
      type_id: getTypeId(),
      cgccpf: shippingCompany.cgccpf,
      social_name: shippingCompany.social_name,
      phone: shippingCompany.phone,
      phone_unmasked: shippingCompany?.phone?.replace(/[^0-9]+/g, ''),
      shipment_link_id: getShipmentLinkId(),
    };

    let isValid = false;
    try {
      await travelShippingCompanyDetailsSchema.validate(
        {
          cgccpf_unmasked: shippingCompany?.cgccpf?.replace(/[^0-9]+/g, ''),
          ...data,
        },
        { abortEarly: false },
      );
      setErrors({});
      isValid = true;
    } catch (error) {
      const errorResponse = {};
      if (error.inner) {
        error.inner.forEach((item) => {
          errorResponse[item.path] = item.message;
        });
      }
      setErrors(errorResponse);
      const firstMessage = error?.inner?.[0]?.message || 'Verifique os campos obrigatórios';
      snackbar.show(<Text>{firstMessage}</Text>, {
        type: 'error',
      });
    }

    if (!isValid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      await api.put(`v3/shipping-company/${shippingCompany.id}/data/create-travel`, {
        data,
      });
      travel.setData((old) => ({ ...old, shippingCompany }));
      snackbar.show(<Text>Salvo com sucesso</Text>, { type: 'success' });
      onClose();
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Erro ao editar transportadora';
      snackbar.show(<Text>{message}</Text>, {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Modal
        size="lg"
        show={isOpen}
        onHide={onClose}
        heading={
          <Text type="header" color="dark" weight="500">
            Editar transportadora
          </Text>
        }
        body={
          <Row>
            <Col md={6} xs={12} className="mb-3">
              <Radio.Group
                label={
                  <Text color="dark" type="label">
                    Tipo de pessoa
                  </Text>
                }
                onChange={(event) => {
                  setShippingCompany({
                    ...shippingCompany,
                    personType: {
                      name: event.target.value,
                    },
                    type_id: event.target.value === 'Jurídica' ? 3 : 2,
                  });
                }}
                value={shippingCompany?.personType?.name}
                horizontal
              >
                <Radio value="Jurídica">
                  <Text color="dark" type="label">
                    Jurídica
                  </Text>
                </Radio>
                <Radio value="Física">
                  <Text color="dark" type="label">
                    Física
                  </Text>
                </Radio>
              </Radio.Group>
            </Col>
            <Col xs={12} className="mb-3">
              <Input
                label={shippingCompany?.personType?.name === 'Jurídica' ? 'Razão social' : 'Nome'}
                value={shippingCompany?.social_name}
                onChange={(event) => {
                  setShippingCompany({
                    ...shippingCompany,
                    social_name: event?.target?.value,
                  });
                }}
                error={errors.social_name}
              />
            </Col>
            <Col md={6} xs={12} className="mb-3">
              <Input
                label={shippingCompany?.personType?.name === 'Jurídica' ? 'CNPJ' : 'CPF'}
                value={shippingCompany?.cgccpf}
                onChange={(event) => {
                  setShippingCompany({
                    ...shippingCompany,
                    cgccpf: event?.target?.value,
                  });
                }}
                masked
                mask={shippingCompany?.personType?.name === 'Jurídica' ? CNPJ_MASK : CPF_MASK}
                error={errors.cgccpf_unmasked}
              />
            </Col>
            <Col md={6}>
              <Select
                label="Vinculo transportador"
                value={shippingCompany?.shipment_link}
                options={linkOption}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                onChange={(value) => {
                  setShippingCompany({
                    ...shippingCompany,
                    shipment_link: value,
                    shipment_link_id: value?.id,
                  });
                }}
                error={errors.shipment_link_id}
              />
            </Col>
            <Col md={6} xs={12}>
              <Input
                label="Telefone"
                value={shippingCompany?.phone}
                onChange={(event) => {
                  setShippingCompany({
                    ...shippingCompany,
                    phone: event?.target?.value,
                  });
                }}
                masked
                mask={PHONE_MASK}
                error={errors.phone_unmasked}
              />
            </Col>
            <Col className="text-center my-3">
              <Button
                className="py-2"
                disabled={
                  !shippingCompany?.social_name ||
                  !shippingCompany?.phone ||
                  !shippingCompany?.cgccpf ||
                  !shippingCompany?.personType
                }
                onClick={() => saveData()}
                loading={loading}
              >
                <Text type="regular" weight={500}>
                  Salvar
                </Text>
              </Button>
            </Col>
          </Row>
        }
      />
    </>
  );
}

export default ShippingCompanyDetails;
